<script lang="ts">
  import PublicProfileSubpageShell from '@/components/profile/PublicProfileSubpageShell.svelte';
  import type { ThemeConfig, ProfileThemeName } from '@/lib/profile-themes';

  interface Profile {
    display_name: string;
    username: string;
    avatar_initial: string;
    avatar_url?: string;
  }

  interface Theme extends ThemeConfig {
    template?: ProfileThemeName;
  }

  interface PresentationItem {
    id: string;
    slug: string;
    title: string;
    description?: string;
    thumbnail_url?: string;
    category?: string;
    presented_at?: string;
  }

  let {
    profile,
    presentations = [],
    theme = { template: 'clean' },
  }: {
    profile: Profile;
    presentations: PresentationItem[];
    theme?: Theme;
  } = $props();

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
  }
</script>

<svelte:head>
  <title>{profile.display_name}님의 발표자료 | AX Hub</title>
  <meta name="description" content="{profile.display_name}님의 발표자료 목록" />
</svelte:head>

<PublicProfileSubpageShell
  {profile}
  {theme}
  backHref={`/@${profile.username}`}
  title={`${profile.display_name}님의 발표자료`}
  subtitle={`발표자료 ${presentations.length}개`}
  width="4xl"
>
  {#if presentations.length === 0}
    <div class="text-center py-16">
      <p class="public-subpage-faint text-lg">아직 등록된 발표자료가 없습니다.</p>
    </div>
  {:else}
    <div class="pt-grid">
      {#each presentations as pt (pt.slug)}
        <a href="/pt/{pt.slug}" class="pt-card" target="_blank" rel="noopener">
          <div class="pt-thumb">
            {#if pt.thumbnail_url}
              <img src={pt.thumbnail_url} alt={pt.title} />
            {:else}
              <div class="pt-placeholder">
                <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="square" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
                </svg>
              </div>
            {/if}
            {#if pt.category}
              <span class="pt-badge">{pt.category}</span>
            {/if}
          </div>
          <div class="pt-info">
            <h3 class="pt-title">{pt.title}</h3>
            {#if pt.description}
              <p class="pt-desc">{pt.description}</p>
            {/if}
            <div class="pt-meta">
              {#if pt.category}
                <span class="pt-category">{pt.category}</span>
              {/if}
              {#if pt.presented_at}
                <span>{formatDate(pt.presented_at)}</span>
              {/if}
            </div>
          </div>
        </a>
      {/each}
    </div>
  {/if}
</PublicProfileSubpageShell>

<style>
  .pt-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media (min-width: 640px) {
    .pt-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .pt-card {
    display: block;
    background: var(--card-bg, rgba(255, 255, 255, 0.92));
    border: 1px solid var(--card-border, rgba(15, 23, 42, 0.1));
    overflow: hidden;
    text-decoration: none;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }

  .pt-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }

  .pt-thumb {
    aspect-ratio: 16/9;
    background: #1a1a1a;
    position: relative;
    overflow: hidden;
  }

  .pt-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s ease;
  }

  .pt-card:hover .pt-thumb img {
    transform: scale(1.03);
  }

  .pt-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #555;
  }

  .pt-badge {
    position: absolute;
    top: 6px;
    left: 6px;
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 600;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    border-radius: 2px;
  }

  .pt-info {
    padding: 10px;
  }

  .pt-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary, #111827);
    line-clamp: 2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
    margin: 0;
  }

  .pt-desc {
    font-size: 11px;
    color: var(--text-secondary, #6b7280);
    margin: 4px 0 0;
    line-clamp: 2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.5;
  }

  .pt-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
    font-size: 11px;
    color: var(--text-tertiary, #9ca3af);
  }

  .pt-category {
    font-weight: 500;
  }
</style>
